import { accessTokenPayloadSchema } from '@scalar-org/entities/token'
import { decode } from '@scalar-org/tokens'

import path from 'node:path'
import os from 'node:os'
import fs from 'node:fs'
import z from 'zod'

import { config } from '@/config'
import { output } from '@/helpers/std-out'
import { generateAccessByPersonalToken } from '@/domains/auth/login/helpers/personal-token-generate-access'

const authSchema = z.union([
  z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
  }),
  z.object({
    accessToken: z.string(),
    personalToken: z.string(),
  }),
])

type Auth = z.infer<typeof authSchema>

const SCALAR_CONFIG_FILE_NAME = '.scalar-config'
export const AUTH_CONFIG_FILE = path.join(os.homedir(), SCALAR_CONFIG_FILE_NAME)

const eventListeners: ((auth: Auth) => void)[] = []
const addEventListener = (callback: (auth: Auth) => void) => {
  eventListeners.push(callback)
}
const fireCallbacks = (auth: Auth) =>
  eventListeners.forEach((callback) => callback(auth))

/**
 * Write auth data to a local file
 */
export const setAuthData = (authData: Auth) => {
  const data = JSON.stringify(authData)
  fs.writeFileSync(AUTH_CONFIG_FILE, data, { mode: 0o600 })
  fireCallbacks(authData)
}

/**
 * Get auth data from local config file.
 *
 * It will show an error message if the user is not authorized.
 *
 * Every time the auth data is read we refresh the accessToken
 */
export const getAuthData = async (opts?: { skipRefresh?: boolean }) => {
  const { skipRefresh = false } = opts ?? {}

  if (!fs.existsSync(AUTH_CONFIG_FILE)) {
    return output
      .error()
      .title('Authorization required')
      .message('Please login before running this command.')
      .exit('error')
  }

  const auth = authSchema.parse(
    JSON.parse(fs.readFileSync(AUTH_CONFIG_FILE, 'utf-8')),
  )

  const { email, role, teamUid, exp } = decode(
    auth.accessToken,
    accessTokenPayloadSchema,
  )

  const isExpired = exp * 1000 < Date.now()

  // we skip refresh the token if option is specified or when the token is actually expired
  if (skipRefresh && !isExpired) {
    return { ...auth, email, role, teamUid, addEventListener }
  }

  // We get a fresh new access token every time
  try {
    if ('refreshToken' in auth) {
      const updatedAuth = await refreshToken(auth.refreshToken)
      return { ...updatedAuth, email, role, teamUid, addEventListener }
    }

    // Refresh using the personal token
    const updatedAuth = await generateAccessByPersonalToken({
      personalToken: auth.personalToken,
    })

    return { ...updatedAuth, email, role, teamUid, addEventListener }
  } catch {
    return output
      .error()
      .title('Expired session')
      .message('Session expired, please login again.')
      .exit('error')
  }
}

/**
 * Delete local configuration file
 */
export const clearAuthData = () => {
  if (!fs.existsSync(AUTH_CONFIG_FILE)) {
    return
  }

  fs.unlinkSync(AUTH_CONFIG_FILE)
}

const refreshTokenResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
})

/**
 * Refresh user token, save it in the local configuration file and return new tokens
 */
export const refreshToken = async (token: string, teamUid?: string) => {
  const result = await fetch(`${config.services.api}/core/login/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      refreshToken: token,
      teamUid,
    }),
  })

  if (result.ok) {
    const { accessToken, refreshToken } = refreshTokenResponseSchema.parse(
      await result.json(),
    )

    setAuthData({ accessToken, refreshToken })
    return { accessToken, refreshToken }
  }

  throw Error('Could not refresh token')
}
